import { useCallback, useEffect, useMemo } from 'react'

import { usePortal } from '../../../../../../app/_providers/PortalProvider.client'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../../../../brunswick/containers/embroidery'
import { useBag } from '../../../../../../lib/hooks/useBag'
import { ProductDetailsContainer } from '../ProductDetailsContainer'

export const useAddToBag = () => {
  const { hasMandatoryLogoEmbroidery, hasMandatoryTextEmbroidery } = usePortal()

  const {
    featuredProduct,
    productGroup,
    isPortalColorSelected,
    isProductEmbroiderable,
    isProductFinalSale,
    isProductOnWaitlist,
    selectedColorKey,
    selectedSize,
    selectedVariant,
    sizes,
  } = ProductDetailsContainer.useContainer()

  const { swPromoEligible } = featuredProduct

  const {
    detailsSection: { addToBagCTA, inFlightAddToBagCTA, soldOutAddToBagCTA, waitlistAddToBagCTA },
    cart: { selectSize },
  } = useTranslation()

  const { selectedEmbroideryFormOptions } = EmbroideryService.useContainer()

  const {
    addToBag,
    addToBagErrors,
    handleAddToBagClick,
    isAddToCartInFlight,
    quantity: quantityVal,
    resetInvalidBagClickStatus,
    setQuantity,
  } = useBag({
    isProductFinalSale,
    isProductWaitlistable: isProductOnWaitlist,
    requiresSizeSelection: sizes.length > 0,
    selectedVariant,
    selectedSize,
    swPromoEligible,
    isPortalColorSelected,
    isProductEmbroiderable,
  })

  const quantity = useMemo(() => quantityVal.toString(), [quantityVal])

  const handleChangeQuantity = useCallback((q: string) => setQuantity(parseInt(q)), [setQuantity])

  const { isSizeErrored, isAddToBagDisabled } = useMemo(() => {
    const isSizeErrored: boolean = !selectedSize && addToBagErrors.missingRequiredSize
    const isAddToBagDisabled =
      addToBagErrors.missingRequiredTextEmbroidery ||
      isSizeErrored ||
      (!!selectedSize?.soldOut && !isProductOnWaitlist) ||
      (featuredProduct.soldOut && !isProductOnWaitlist) ||
      isAddToCartInFlight
    return { isAddToBagDisabled, isSizeErrored }
  }, [
    addToBagErrors.missingRequiredSize,
    addToBagErrors.missingRequiredTextEmbroidery,
    featuredProduct.soldOut,
    isAddToCartInFlight,
    isProductOnWaitlist,
    selectedSize,
  ])

  const addToBagCta = useMemo(() => {
    if (isAddToCartInFlight) return inFlightAddToBagCTA
    if (isProductOnWaitlist) return waitlistAddToBagCTA
    if (selectedSize?.soldOut || featuredProduct.soldOut) return soldOutAddToBagCTA
    if (isSizeErrored) return selectSize
    return addToBagCTA
  }, [
    addToBagCTA,
    featuredProduct.soldOut,
    inFlightAddToBagCTA,
    isAddToCartInFlight,
    isProductOnWaitlist,
    isSizeErrored,
    selectSize,
    selectedSize?.soldOut,
    soldOutAddToBagCTA,
    waitlistAddToBagCTA,
  ])

  const hasMandatoryTextAndLogoEmbroidery = useMemo(() => {
    return (
      hasMandatoryTextEmbroidery(productGroup.category) ||
      hasMandatoryLogoEmbroidery(productGroup.category)
    )
  }, [hasMandatoryLogoEmbroidery, hasMandatoryTextEmbroidery, productGroup.category])

  /**
   * Reset bag errors if:
   * - Selected color or size is changed
   * - A portal with mandatory text & logo is selected, and the text embroidery changes
   */
  useEffect(resetInvalidBagClickStatus, [
    resetInvalidBagClickStatus,
    selectedColorKey,
    selectedSize,
  ])

  useEffect(() => {
    if (isPortalColorSelected && hasMandatoryTextAndLogoEmbroidery) resetInvalidBagClickStatus()
  }, [
    selectedEmbroideryFormOptions.textFirstLine,
    isPortalColorSelected,
    hasMandatoryTextAndLogoEmbroidery,
    resetInvalidBagClickStatus,
  ])

  return {
    addToBag,
    addToBagCta,
    addToBagErrors,
    isAddToBagDisabled,
    isAddToCartInFlight,
    isSizeErrored,
    onAddToBagClick: handleAddToBagClick,
    onChangeQuantity: handleChangeQuantity,
    quantity,
    resetInvalidBagClickStatus,
  }
}
